import Minilog from 'minilog';
import {createMergeAction} from './base';
import {V2Resource, RequestPromise, V2ListModel, Readings} from './services';
import {mergeObjects} from './core/objects';
import * as _ from 'underscore';
import moment from 'moment';
import {TEXT_ERROR_IN_LOADING_READINGS} from './locale';
import {READINGS_UPDATE, READINGS_UPDATE_LATEST} from "./redux/data";

let logger = Minilog('frontend.models.cabinets');

export interface CabinetAreaFilter {
    gateway__premises__region?: string;
    gateway__premises__region__country?: string;
    gateway__premises?: Number;
    gateway__premises__slug?: string;
}

export interface CabinetParameters extends CabinetAreaFilter {
    page?: number;
    premises?: string;
    disable_alarm_count?: boolean;
    page_size?: Number;
    standalone?: boolean;

}

export interface CabinetSensor {
    "key": any;
    "sensor_description": string;
    "enabled": boolean;
}


export interface V2Cabinet {
    unit_id: string;
    description: string;
    gateway: any;
    resource_uri: string;
    sensors: Array<CabinetSensor>;
}

export interface V3Cabinet {
    unit_id: string;
    description: string;
    gateway: any;
    resource_uri: string;
    sensors: Array<CabinetSensor>;
    dashboard_display: boolean;
    subscribed: boolean;
}

export interface CabinetsPatch {
    subscribed: boolean;
    dashboard_display: boolean;
}

/**
 * For handling all cabinet v2 API calls
 */
class CabinetV2Resource extends V2Resource {
    constructor() {
        super(V2Resource.UNIT);
    }

    list(params?: CabinetParameters) : RequestPromise<V2ListModel<V2Cabinet>> {
        // inject standard params that v2 API expects
        let allParams = mergeObjects(
            {display: true, limit: 15, offset: 0}, {}, params
        );
        return super.list(allParams);
    }
}

/**
 * Global cabinet API instance
 *
 * @type {CabinetV2Resource}
 */
export let CabinetAPI: CabinetV2Resource = new CabinetV2Resource();

/**
 * Replace: data for premises units
 */
export function updateSensorList(cabinetInfo: any) {
    return createMergeAction(
        'cabinetinfo_set',
        {data: {premisesUnits: cabinetInfo.data}},
        {array_replace: true});
}

function updateCabinetsList(cabinetInfo: any) {
    return createMergeAction(
        'cabinet_listing',
        {data: {units: cabinetInfo.data}},
        {array_replace: true});
}

/**
 * Merge: enable loading progress for cabinet export modal
 */
export const CABINETS_LOADING = 'cabinets_loading';
export const SENSORS_LOADING = 'sensors_loading';
function enableCabinetsLoad() {
    return {type: CABINETS_LOADING, to: true};
}

/**
 * Merge: disable loading progress for cabinet export modal
 */
function disableCabinetsLoad() {
    return {type: CABINETS_LOADING, to: false};
}

/**
 * Merge: enable loading progress for cabinet export modal
 */
function enableReadingsLoad(unitId = null, details) {
    return {type: SENSORS_LOADING, unit: unitId, to: true, details: details};
}

/**
 * Merge: disable loading progress for cabinet export modal
 */
function disableReadingsLoad(unitId, details) {
    return {type: SENSORS_LOADING, unit: unitId, to: false, details: details};
}

// get a list of potentially filtered cabinets
export function getCabinetsData(params?: CabinetParameters, callback = updateCabinetsList, all = false) : Function {
    return function (dispatch) {
        dispatch(enableCabinetsLoad());
        let parameters = params || {};
        if (all) parameters.page_size = 1000;
        let requestFn = all ?
            CabinetAPI.listAll.bind(CabinetAPI) :
            CabinetAPI.list.bind(CabinetAPI);
        return requestFn(parameters).then(function(response) {
            dispatch(callback(response));
            dispatch(disableCabinetsLoad());
            return Promise.resolve(response);
        }).catch(function() {
            dispatch(disableCabinetsLoad());
        });
    };
}

// Get a list of all units in a given premises
export function getCabinetDataForPremise(premises: Number) : Function {
    return getCabinetsData({gateway__premises: premises}, updateSensorList, true);
}

export function getCabinetsDataById(id: string) : Function {
    return function (dispatch) {
        dispatch(enableCabinetsLoad());
        let options = {
            uriPostfix: id
        };
        return CabinetAPI.request('GET', options)
            .then(function (response) {
            let data = {
                data: {
                    count: 1,
                    results: [response.data],
                }
            };
            dispatch(updateCabinetsList(data));
            dispatch(disableCabinetsLoad());
            return Promise.resolve(response);
        }).catch(function () {
            dispatch(disableCabinetsLoad());
        });
    };
}

export interface SensorsParameters {
    unit_id: string;
    forDetails?: boolean;
    live?: boolean;
    options: {
        from?: any;
        to?: any;
        langcode?: string;
        sensors?: string[];
    };
}

export let ReadingsAPI: any = new Readings();

function updateSensorReadings(id, data, forDetails) {
    return {type: forDetails ? READINGS_UPDATE : READINGS_UPDATE_LATEST, unit: id, data};
}

export function resetSensorUIStore() {
    return {type: SENSORS_LOADING};
}

export function resetSensorStore() {
    return {
        type: READINGS_UPDATE,
        data: {sensors: {}},
        reset: true
    };
}

/**
 * Get the readings for a cabinet
 */
export function getSensorsData(params: SensorsParameters) : Function {
    let postFix = params.unit_id;
    return function (dispatch) {
        dispatch(enableReadingsLoad(postFix, params.forDetails));
        return ReadingsAPI.request('GET', {uriPostfix: postFix + "/", params: params.options})
            .then(function(response) {
                logger.debug('Retrieved sensor data ', JSON.stringify(response.data));
                if (params.forDetails) {
                    dispatch(updateSensorReadings(params.unit_id, response.data, true));
                } else {
                    let enabledSensors = _.pluck(
                        _.filter(response.data.sensors, (sensor: any) => sensor.enabled),
                        "key"
                    );
                    let data = processReadings(params.unit_id, response.data, enabledSensors);
                    dispatch(updateSensorReadings(params.unit_id, data, false));
                }
                dispatch(disableReadingsLoad(postFix, params.forDetails));
                return Promise.resolve();
            }).catch(function () {
                logger.warn('Failed to retrieve sensor data for ', params);
                dispatch(updateSensorReadings(
                    params.unit_id, {error: TEXT_ERROR_IN_LOADING_READINGS}, params.forDetails
                ));
                dispatch(disableReadingsLoad(postFix, params.forDetails));
                return Promise.reject();
            });
    };
}

function setCabinetsDashboardDisplayChanging(id, value: boolean) {
    return createMergeAction(
        'premises_dashboard_display_updating',
        {
            ui: {cabinets: {[id]: {settingDashboardDisplay: value}}}
        }
    );
}

function setCabinetsSubscibeDisplayChanging(id, value: boolean) {
    return createMergeAction(
        'premises_dashboard_display_updating',
        {
            ui: {cabinets: {[id]: {isSubscribing: value}}}
        }
    );
}

export const CABINETS_SET_DASHBOARD_DISPLAY = 'cabinets_set_dashboard_display';
export const CABINETS_SET_SUBSCRIBE = 'cabinets_set_subscribe';
export function setCabinetDashboardDisplay(id, dashboard_display: boolean) {
    return { type: CABINETS_SET_DASHBOARD_DISPLAY, id, dashboard_display };
}
export function setCabinetSubscribeDisplay(id, subscribed: boolean) {
    return { type: CABINETS_SET_SUBSCRIBE, id, subscribed };
}

export function updateCabinetDashboardDisplay(id, showOnDashboard) {
    return function (dispatch) {
        dispatch(setCabinetsDashboardDisplayChanging(id, true));
        return CabinetAPI.update<CabinetsPatch>(id, {dashboard_display: showOnDashboard})
            .then(function (r) {
                dispatch(setCabinetDashboardDisplay(id, r.data.dashboard_display));
                dispatch(setCabinetsDashboardDisplayChanging(id, false));
            })
            .catch(() => {
                dispatch(setCabinetsDashboardDisplayChanging(id, false));
            });
    };
}

export function updateCabinetSubscribeDisplay(id, subscribed) {
    return function (dispatch) {
        dispatch(setCabinetsSubscibeDisplayChanging(id, true));
        return CabinetAPI.update<CabinetsPatch>(id, {subscribed: subscribed})
            .then(function (r) {
                dispatch(setCabinetSubscribeDisplay(id, r.data.subscribed));
                dispatch(setCabinetsSubscibeDisplayChanging(id, false));
            })
            .catch(() => {
                dispatch(setCabinetsSubscibeDisplayChanging(id, false));
            });
    };
}

function getDisplayReading(description, key) {
    if (description.localized_readings && (key in description.localized_readings)) {
        return description.localized_readings[key];
    }
    return description.readings[key];
}

// Process readings before sending them to the reducers
function processReadings(unit, data, enabledSensors) {
    let toMerge = {};
    if (data.error) {
        toMerge[unit] = {error: data.error};
    } else if (unit) {
        let latest = {};
        let idx = 0;
        while (enabledSensors.length && idx < data.objects.length) {
            let i = enabledSensors.length;
            while (i--) {
                let sensor_key = enabledSensors[i];
                if (sensor_key in data.objects[idx].readings) {
                    latest[sensor_key] = getDisplayReading(data.objects[idx], sensor_key);
                    enabledSensors.splice(i, 1);
                }
            }
            idx++;
        }
        let newReadingsObj: any = [];
        if (data.objects.length) {
            newReadingsObj = mergeObjects(
                {}, {}, data.objects[0], {latestReadings: latest}
            );
        }
        toMerge[unit] = {
            readings: newReadingsObj
        };
    }
    return toMerge;
}


// Batch dispatch to get readings

function fetchReadings(unit: any, language, enabledSensors) {
    let options = {
        from: Math.floor(moment.now() / 1000) - (8 * (60 * 60)),
        to: Math.floor(moment.now() / 1000),
        sensors: enabledSensors,
        langcode: language
    };
    return ReadingsAPI.request('GET', {uriPostfix: unit.unit_id + "/", params: options})
        .then(function (response) {
            return {unit_id: unit.unit_id, response, enabledSensors};
        });
}

export function getSensorsDataInMultiples(unitList, language) : Function {
    return function (dispatch) {
        try {
            dispatch(enableReadingsLoad(null, true));
            let promises = [];
            _.each(unitList, function (unit: any) {
                let curUnit = unit;
                do {
                    let enabledSensors = _.pluck(
                        _.filter(unit.sensors, (sensor: any) => sensor.enabled),
                        "key");
                    promises.push(fetchReadings(unit, language, enabledSensors));
                    curUnit = curUnit.child;
                } while (curUnit);
            });
            return Promise.all(promises).then((responses) => {
                let data = {};
                _.each(responses, ({response, unit_id, enabledSensors}) => {
                    mergeObjects(data, {}, processReadings(unit_id, response.data, enabledSensors));
                });
                dispatch(updateSensorReadings(null, data, false));
                dispatch(disableReadingsLoad(null, true));
                return Promise.resolve();
            });
        } catch (e) {
            dispatch(disableReadingsLoad(null, true));
        }
    };
}
